import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import toast from '../../../../utils/toast';

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc'
}

export interface BlacklistedMobileNumberItem {
  id: string;
  mobile_number: string;
  reason: string;
  created_at: string;
  active: boolean;
}

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BlacklistedMobileNumberItem[];
}

const PAGE_SIZE = 25;

export const useBlacklistedMobileNumbers = (
  searchQuery: string,
  page: number,
  sortByField?: string,
  sortOrder?: SortOrder,
  activeOnly?: boolean
) => {
  const [data, setData] = useState<BlacklistedMobileNumberItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);

  const fetchBlacklistedMobileNumbers = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get<ApiResponse>(`${environment.baseUrl}/blacklisted-mobile-numbers/`, {
        params: {
          page,
          page_size: PAGE_SIZE,
          search: searchQuery || undefined,
          sort_by: sortByField || undefined,
          sort_order: sortOrder || undefined,
          active_only: activeOnly || undefined,
        },
      });

      setData(response.data.results);
      setTotalPages(Math.ceil(response.data.count / PAGE_SIZE));
      setTotal(response.data.count);
    } catch {
      toast.error('Failed to fetch blacklisted mobile numbers');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, sortByField, sortOrder, activeOnly]);

  useEffect(() => {
    fetchBlacklistedMobileNumbers();
  }, [fetchBlacklistedMobileNumbers]);

  const addItem = useCallback((newItem: BlacklistedMobileNumberItem) => {
    setData(prevData => [newItem, ...prevData]);
    setTotal(prevTotal => prevTotal + 1);
  }, []);

  const updateItem = useCallback((updatedItem: BlacklistedMobileNumberItem) => {
    setData(prevData =>
      prevData.map(item =>
        item.id === updatedItem.id ? updatedItem : item
      )
    );
  }, []);

  return {
    data,
    loading,
    totalPages,
    total,
    refetch: fetchBlacklistedMobileNumbers,
    addItem,
    updateItem
  };
};